import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Trash2, Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API = '/api';
const PAGE_SIZE = 50;

// ReservedUsernamesList - admin panel for the /{username} blocklist.
// Backed by /api/admin/reserved-usernames. The service auto-seeds ~500
// major brands, celebrities, sports orgs, and abuse terms on startup;
// this panel is for adding whatever the seed missed (new viral names,
// competitor squats, protected clients, etc.) at runtime.
export const ReservedUsernamesList = () => {
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [newReason, setNewReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: String(offset),
            });
            if (q) params.set('q', q);
            const res = await fetch(`${API}/admin/reserved-usernames?${params}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setEntries(data.entries || []);
                setTotal(data.total || 0);
            } else {
                toast.error('Could not load reserved usernames');
            }
        } catch (err) {
            toast.error('Network error loading reserved usernames');
        }
        setLoading(false);
    }, [q, offset]);

    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    const handleAdd = async (e) => {
        e?.preventDefault?.();
        const candidate = newUsername.trim().toLowerCase();
        if (!candidate) return;
        setSaving(true);
        try {
            const res = await fetch(`${API}/admin/reserved-usernames`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ username: candidate, reason: newReason.trim() }),
            });
            if (res.ok) {
                toast.success(`Reserved @${candidate}`);
                setNewUsername('');
                setNewReason('');
                setAddOpen(false);
                setOffset(0);
                fetchEntries();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Could not reserve username');
            }
        } catch (err) {
            toast.error('Network error');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget) return;
        try {
            const res = await fetch(
                `${API}/admin/reserved-usernames/${encodeURIComponent(removeTarget.username)}`,
                { method: 'DELETE', credentials: 'include' },
            );
            if (res.ok) {
                toast.success(`Unreserved @${removeTarget.username}`);
                fetchEntries();
            } else {
                toast.error('Could not remove');
            }
        } catch (err) {
            toast.error('Network error');
        } finally {
            setRemoveTarget(null);
        }
    };

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="reserved-usernames-panel">
            {/* Header: title + search + add */}
            <div className="px-4 py-3 border-b border-border flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Reserved Usernames ({total})
                </h2>
                <div className="flex-1 flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setOffset(0); }}
                            placeholder="Filter by prefix…"
                            className="h-8 pl-8 text-xs rounded-lg"
                            data-testid="reserved-search-input"
                        />
                    </div>
                    <Button
                        size="sm"
                        onClick={() => setAddOpen(true)}
                        className="h-8 gap-1.5"
                        data-testid="reserved-add-btn"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add
                    </Button>
                </div>
            </div>

            {/* Add form (inline expandable, keeps table + form on one panel) */}
            {addOpen && (
                <form
                    onSubmit={handleAdd}
                    className="px-4 py-3 border-b border-border bg-secondary/40 flex flex-col sm:flex-row gap-2"
                    data-testid="reserved-add-form"
                >
                    <Input
                        autoFocus
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
                        placeholder="username (e.g. taylorswift)"
                        maxLength={24}
                        spellCheck={false}
                        className="font-mono h-8 text-xs flex-1"
                        data-testid="reserved-add-username"
                    />
                    <Input
                        value={newReason}
                        onChange={(e) => setNewReason(e.target.value)}
                        placeholder="reason (optional)"
                        maxLength={200}
                        className="h-8 text-xs flex-1"
                        data-testid="reserved-add-reason"
                    />
                    <div className="flex gap-1">
                        <Button
                            type="submit"
                            size="sm"
                            disabled={saving || !newUsername.trim()}
                            className="h-8"
                            data-testid="reserved-add-submit"
                        >
                            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                            Reserve
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => { setAddOpen(false); setNewUsername(''); setNewReason(''); }}
                            className="h-8"
                            data-testid="reserved-add-cancel"
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            )}

            {/* Table body */}
            {loading ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : entries.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                    {q ? `No reserved usernames matching "${q}"` : 'No reserved usernames yet'}
                </div>
            ) : (
                <div className="divide-y divide-border" data-testid="reserved-list">
                    {entries.map((entry) => (
                        <div
                            key={entry.username}
                            className="px-4 py-2.5 flex items-center gap-3 hover:bg-secondary/40 transition-colors"
                            data-testid={`reserved-row-${entry.username}`}
                        >
                            <span className="text-sm font-mono text-foreground flex-shrink-0 w-40 truncate">
                                @{entry.username}
                            </span>
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground uppercase tracking-wide flex-shrink-0">
                                {entry.category || 'other'}
                            </span>
                            <span className="text-xs text-muted-foreground flex-1 truncate">
                                {entry.reason}
                            </span>
                            <button
                                type="button"
                                onClick={() => setRemoveTarget(entry)}
                                className="text-muted-foreground hover:text-red-600 p-1 flex-shrink-0"
                                title="Unreserve"
                                data-testid={`reserved-remove-${entry.username}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                        Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant="outline" size="sm" className="h-7 text-xs"
                            disabled={offset === 0}
                            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                            data-testid="reserved-prev-page"
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline" size="sm" className="h-7 text-xs"
                            disabled={offset + PAGE_SIZE >= total}
                            onClick={() => setOffset((o) => o + PAGE_SIZE)}
                            data-testid="reserved-next-page"
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}

            {/* Delete confirmation */}
            <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
                <AlertDialogContent data-testid="reserved-remove-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unreserve @{removeTarget?.username}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This makes <span className="font-mono">@{removeTarget?.username}</span> claimable
                            by any user. If it&apos;s a brand or celebrity handle, keep it reserved unless
                            you&apos;re certain.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRemove}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="reserved-remove-confirm"
                        >
                            Unreserve
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default ReservedUsernamesList;
